/**
 * Promise wrappers around amazon-cognito-identity-js, plus the two things the
 * UI needs that the SDK does not give it: the pool's password policy in a form
 * a form can render, and a translation from AWS exception names to sentences a
 * creator can act on. Nothing outside this module touches the SDK, so the whole
 * auth surface can be reasoned about in one file.
 */

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { getConfig } from '../config';

let pool: CognitoUserPool | null = null;

/**
 * Built on first use rather than at module load: the pool ids arrive with
 * config.json, and importing this module must not require that fetch to have
 * finished.
 */
function userPool(): CognitoUserPool {
  if (!pool) {
    const cfg = getConfig();
    pool = new CognitoUserPool({ UserPoolId: cfg.userPoolId, ClientId: cfg.userPoolClientId });
  }
  return pool;
}

function cognitoUser(email: string): CognitoUser {
  return new CognitoUser({ Username: email, Pool: userPool() });
}

export interface SessionInfo {
  email: string;
  /** Cognito sub — the same id the API stores as `Profile.userId`. */
  sub: string;
  idToken: string;
}

function fromSession(session: CognitoUserSession): SessionInfo {
  const claims = session.getIdToken().decodePayload() as Record<string, unknown>;
  return {
    email: String(claims['email'] ?? ''),
    sub: String(claims['sub'] ?? ''),
    idToken: session.getIdToken().getJwtToken(),
  };
}

// ---------------------------------------------------------------------------
// Password policy
// ---------------------------------------------------------------------------

export const PASSWORD_MIN_LENGTH = 8;

export interface PasswordRule {
  id: string;
  label: string;
  test: (value: string) => boolean;
}

/**
 * A mirror of the user pool's password policy (infra/lib/ugc-stack.ts), kept
 * next to the sign-up call it guards so the two are edited in the same breath.
 * The signup and reset forms check against this so nobody has to learn the
 * rules from a rejected submit.
 */
export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    id: 'length',
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    test: (value) => value.length >= PASSWORD_MIN_LENGTH,
  },
  { id: 'lowercase', label: 'A lowercase letter', test: (value) => /[a-z]/.test(value) },
  { id: 'uppercase', label: 'An uppercase letter', test: (value) => /[A-Z]/.test(value) },
  { id: 'digit', label: 'A number', test: (value) => /[0-9]/.test(value) },
];

export function passwordMeetsPolicy(value: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(value));
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Failures this module raises itself, for the flows where the SDK would
 * otherwise leave a promise pending forever. Named like a Cognito exception so
 * every call site keeps a single catch.
 */
class AuthFlowError extends Error {
  constructor(name: string, message: string) {
    super(message);
    this.name = name;
  }
}

/**
 * The exception name for an error from the SDK. It sets both `code` and `name`
 * for service errors but only `code` for a failed request, so `code` wins.
 */
export function cognitoErrorName(error: unknown): string {
  if (typeof error !== 'object' || error === null) return '';
  const candidate = error as { code?: unknown; name?: unknown };
  if (typeof candidate.code === 'string') return candidate.code;
  if (typeof candidate.name === 'string') return candidate.name;
  return '';
}

/** Sign-in hit an account that exists but never confirmed its email. */
export function isUserNotConfirmed(error: unknown): boolean {
  return cognitoErrorName(error) === 'UserNotConfirmedException';
}

/** Which call failed — the same exception means different things per flow. */
export type AuthAction =
  | 'signIn'
  | 'signUp'
  | 'confirm'
  | 'resend'
  | 'forgotPassword'
  | 'resetPassword'
  | 'changePassword';

const FALLBACK_MESSAGES: Record<AuthAction, string> = {
  signIn: 'Could not sign you in. Try again.',
  signUp: 'Could not create your account. Try again.',
  confirm: 'Could not confirm that code. Try again.',
  resend: 'Could not send a new code. Try again in a moment.',
  forgotPassword: 'Could not start a password reset. Try again.',
  resetPassword: 'Could not set your new password. Try again.',
  changePassword: 'Could not change your password. Try again.',
};

/**
 * Turns any auth failure into a sentence that names the next move. AWS
 * exception text is never shown: it leaks internals, it is written for
 * operators, and for this pool it is deliberately vague about whether an
 * account exists — a vagueness the UI must preserve rather than explain away.
 */
export function authErrorMessage(error: unknown, action: AuthAction): string {
  // Confirming or resending against an account that is already verified comes
  // back as two different exceptions whose only distinguishing mark is the
  // message. Caught here so that flow ends in "sign in" rather than in advice
  // to request another code that will never work.
  if (action === 'confirm' || action === 'resend') {
    const detail = error instanceof Error ? error.message : '';
    if (/already confirmed|status is CONFIRMED/i.test(detail)) {
      return 'This email is already confirmed. Sign in instead.';
    }
  }

  switch (cognitoErrorName(error)) {
    case 'NetworkError':
      return 'Could not reach the server. Check your connection and try again.';
    case 'CodeMismatchException':
      return 'That code is not correct. Check the six digits in the email and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Send yourself a new one.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'InvalidPasswordException':
      return `Choose a password with at least ${PASSWORD_MIN_LENGTH} characters, including an uppercase letter, a lowercase letter and a number.`;
    case 'UsernameExistsException':
      return 'An account already exists for that email. Sign in instead, or reset your password.';
    case 'UserNotConfirmedException':
      return 'This email has not been confirmed yet. Enter the code we sent you.';
    case 'PasswordResetRequiredException':
      return 'This account needs a new password. Use the "Forgot your password?" link to set one.';
    case 'AliasExistsException':
      return 'That email is already confirmed on another account.';
    case 'CodeDeliveryFailureException':
      return 'We could not send that email. Check the address and try again.';
    case 'InvalidParameterException':
      return 'Check the details you entered and try again.';
    case 'UserNotFoundException':
      // The pool has preventUserExistenceErrors on, so this should not reach
      // sign-in or reset at all. Answer without confirming either way.
      return 'We could not continue with that email. Check it and try again.';
    case 'NotAuthorizedException':
      return notAuthorizedMessage(error, action);
    default:
      if (error instanceof AuthFlowError) return error.message;
      return FALLBACK_MESSAGES[action];
  }
}

/**
 * Cognito reuses NotAuthorizedException for a wrong password, a locked-out
 * account and a spent reset code. The distinguishing detail is only in the
 * message, which is read here and never rendered.
 */
function notAuthorizedMessage(error: unknown, action: AuthAction): string {
  const detail = error instanceof Error ? error.message : '';
  if (/attempts exceeded/i.test(detail)) {
    return 'Too many failed attempts. Wait a few minutes before trying again.';
  }
  switch (action) {
    case 'signIn':
      // preventUserExistenceErrors folds "no such account" into this exception,
      // so the wording must cover both without separating them.
      return 'That email and password do not match an account.';
    case 'changePassword':
      return 'Your current password is not correct.';
    default:
      return 'That code can no longer be used. Send yourself a new one and try again.';
  }
}

// ---------------------------------------------------------------------------
// Sign-up and confirmation
// ---------------------------------------------------------------------------

export function signUp(email: string, password: string): Promise<void> {
  return new Promise((resolve, reject) => {
    userPool().signUp(
      email,
      password,
      [new CognitoUserAttribute({ Name: 'email', Value: email })],
      [],
      (err) => (err ? reject(err) : resolve()),
    );
  });
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).confirmRegistration(code, true, (err) => (err ? reject(err) : resolve()));
  });
}

export function resendCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).resendConfirmationCode((err) => (err ? reject(err) : resolve()));
  });
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export function signIn(email: string, password: string): Promise<SessionInfo> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess: (session) => resolve(fromSession(session)),
        onFailure: (err: unknown) => reject(err),
        // The pool sends invitation emails with a temporary password, and that
        // path answers with a challenge instead of an error. Without this the
        // promise would never settle and the form would spin forever.
        newPasswordRequired: () =>
          reject(
            new AuthFlowError(
              'NewPasswordRequired',
              'This account still has its temporary password. Use the "Forgot your password?" link to set your own.',
            ),
          ),
      },
    );
  });
}

/** The stored session, refreshed if needed, or null when there is none. */
export function currentSession(): Promise<SessionInfo | null> {
  return new Promise((resolve) => {
    const user = userPool().getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve(fromSession(session));
    });
  });
}

let pendingToken: Promise<string> | null = null;

/**
 * A currently-valid ID token for the API client.
 *
 * `getSession` transparently spends the 30-day refresh token when the 1-hour ID
 * token has expired, so a creator who leaves the editor open over lunch does
 * not lose the save they come back to. The in-flight promise is shared because
 * the editor fires several requests at once and they should wait on one refresh
 * rather than race four.
 *
 * Resolves to '' instead of rejecting when there is no usable session: the API
 * client then sends no Authorization header and lets the server answer 401, so
 * "signed out" and "session expired" travel the same path to the UI instead of
 * one of them becoming an unhandled rejection mid-render.
 */
export function getIdToken(): Promise<string> {
  if (!pendingToken) {
    pendingToken = readIdToken().finally(() => {
      pendingToken = null;
    });
  }
  return pendingToken;
}

function readIdToken(): Promise<string> {
  return new Promise((resolve) => {
    const user = userPool().getCurrentUser();
    if (!user) return resolve('');
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve('');
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export function signOut(): void {
  userPool().getCurrentUser()?.signOut();
  // Drop any refresh already in flight so it cannot hand a token to a request
  // fired after the user asked to be signed out.
  pendingToken = null;
}

// ---------------------------------------------------------------------------
// Password reset
// ---------------------------------------------------------------------------

/** Asks Cognito to email a reset code. Never reveals whether the email is known. */
export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).forgotPassword({
      // No inputVerificationCode callback on purpose: the SDK routes the
      // delivery details there instead of to onSuccess when one is present, and
      // this flow only needs to know the request was accepted.
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/** Changes the password of the signed-in user, who must supply the old one. */
export function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = userPool().getCurrentUser();
    if (!user) return reject(signedOutError());

    // ChangePassword is authorised by the access token, and the cached one may
    // be up to an hour old; refresh first rather than translate a 401 after.
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return reject(signedOutError());
      user.changePassword(oldPassword, newPassword, (changeErr) =>
        changeErr ? reject(changeErr) : resolve(),
      );
    });
  });
}

function signedOutError(): AuthFlowError {
  return new AuthFlowError(
    'NotSignedIn',
    'Your session has ended. Sign in again to change your password.',
  );
}
